import axios, { type AxiosError } from 'axios'
import { useAuthStore } from '@/entities/auth/store'
import router from '@/app/router/router'
import * as Sentry from '@sentry/vue'

const AxiosInstanceWeb = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/v1/`,
  timeout: 10000,
})

AxiosInstanceWeb.interceptors.request.use(
  async (config) => {
    const globalAuthStore = useAuthStore()
    const token = globalAuthStore.getAccessToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

AxiosInstanceWeb.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      await handleUnauthorized()
    } else if (error.response && error.response.status >= 500) {
      reportServerError(error)
    } else if (!error.response) {
      reportNetworkError(error)
    }

    return Promise.reject(error)
  },
)

async function handleUnauthorized() {
  const globalAuthStore = useAuthStore()
  globalAuthStore.clearTokens()

  const currentPath = router.currentRoute.value.fullPath

  if (currentPath.includes('/?redirect')) {
    await router.push(currentPath)
  } else {
    await router.push({ path: '/', query: { redirect: currentPath } })
  }
}

function reportServerError(error: AxiosError) {
  Sentry.captureException(error, {
    tags: {
      endpoint: error.config?.url,
      method: error.config?.method,
    },
    extra: {
      status: error.response?.status,
      responseData: error.response?.data,
    },
  })
}

function reportNetworkError(error: AxiosError) {
  Sentry.captureException(error, {
    tags: { errorType: 'network' },
    extra: { url: error.config?.url },
  })
}

export default AxiosInstanceWeb
